import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { usePendingVenues } from "./use-pending-venues";
import { useApprovalMetrics } from "./use-approval-metrics";
import { VenueQueueSearch } from "./VenueQueueSearch";
import { VenueQueueCard } from "./VenueQueueCard";
import { VenueQueueEmpty } from "./VenueQueueEmpty";
import { VenueDetailDrawer } from "./VenueDetailDrawer";
import { ApproveVenueDialog } from "./ApproveVenueDialog";
import { RejectVenueDialog } from "./RejectVenueDialog";
import { RequestChangesDialog } from "./RequestChangesDialog";
import { ApprovalMetricsStrip } from "./ApprovalMetricsStrip";
import { VenueApprovalSkeleton } from "./VenueApprovalSkeleton";
import type { PendingVenue } from "./types";

export function VenueApprovalPage() {
  const { t } = useTranslation();
  const { data: venues, isLoading, isError } = usePendingVenues();
  const metrics = useApprovalMetrics();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);

  const filteredVenues = useMemo(() => {
    if (!venues) return venues;
    const term = debouncedSearch.trim().toLowerCase();
    if (term.length === 0) return venues;
    return venues.filter((venue) =>
      [venue.name, venue.city, venue.state, venue.address].some((field) =>
        field.toLowerCase().includes(term),
      ),
    );
  }, [venues, debouncedSearch]);

  const [selectedVenue, setSelectedVenue] = useState<PendingVenue | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [approveTarget, setApproveTarget] = useState<PendingVenue | null>(null);
  const [rejectTarget, setRejectTarget] = useState<PendingVenue | null>(null);
  const [requestChangesTarget, setRequestChangesTarget] = useState<PendingVenue | null>(null);

  const handleSelect = useCallback((venue: PendingVenue) => {
    setSelectedVenue(venue);
    setDrawerOpen(true);
  }, []);

  const handleApproveIntent = useCallback((venue: PendingVenue) => {
    setDrawerOpen(false);
    setApproveTarget(venue);
  }, []);

  const handleRejectIntent = useCallback((venue: PendingVenue) => {
    setDrawerOpen(false);
    setRejectTarget(venue);
  }, []);

  const handleRequestChangesIntent = useCallback((venue: PendingVenue) => {
    setDrawerOpen(false);
    setRequestChangesTarget(venue);
  }, []);

  const handleActionSuccess = useCallback(() => {
    setSelectedVenue(null);
    setApproveTarget(null);
    setRejectTarget(null);
    setRequestChangesTarget(null);
  }, []);

  return (
    <div data-testid="venue-approval-screen" className="space-y-6">
      <header className="space-y-2">
        <p className="brand-eyebrow">{t("venues.eyebrow")}</p>
        <h1 className="text-4xl">{t("venues.title")}</h1>
        <p className="font-sans text-base font-normal text-muted-foreground">
          {t("venues.subtitle")}
        </p>
      </header>

      {metrics.data && <ApprovalMetricsStrip metrics={metrics.data} />}

      {venues && venues.length > 0 && <VenueQueueSearch query={search} onQueryChange={setSearch} />}

      {venues && venues.length > 0 && (
        <p className="font-sans text-sm font-medium text-muted-foreground">
          {t("venues.queue.count", { count: venues.length })}
        </p>
      )}

      {isLoading && <VenueApprovalSkeleton />}

      {isError && (
        <div
          data-testid="venue-queue-error"
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center"
        >
          <p className="font-sans text-sm font-medium text-destructive">
            {t("venues.queue.error")}
          </p>
        </div>
      )}

      {venues && venues.length === 0 && <VenueQueueEmpty />}

      {filteredVenues && filteredVenues.length > 0 && (
        <div className="space-y-3">
          {filteredVenues.map((venue) => (
            <VenueQueueCard key={venue.id} venue={venue} onSelect={handleSelect} />
          ))}
        </div>
      )}

      {venues && venues.length > 0 && filteredVenues && filteredVenues.length === 0 && (
        <div
          data-testid="venue-queue-no-results"
          className="rounded-lg border border-border bg-muted/30 p-6 text-center"
        >
          <p className="font-sans text-sm font-medium text-muted-foreground">
            {t("venues.search.noResults", { query: debouncedSearch.trim() })}
          </p>
        </div>
      )}

      <VenueDetailDrawer
        venue={selectedVenue}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onApprove={handleApproveIntent}
        onReject={handleRejectIntent}
        onRequestChanges={handleRequestChangesIntent}
      />

      <ApproveVenueDialog
        venue={approveTarget}
        open={approveTarget !== null}
        onOpenChange={(open) => {
          if (!open) setApproveTarget(null);
        }}
        onSuccess={handleActionSuccess}
      />

      <RejectVenueDialog
        venue={rejectTarget}
        open={rejectTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRejectTarget(null);
        }}
        onSuccess={handleActionSuccess}
      />

      <RequestChangesDialog
        venue={requestChangesTarget}
        open={requestChangesTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRequestChangesTarget(null);
        }}
        onSuccess={handleActionSuccess}
      />
    </div>
  );
}
